/** Eine Nachricht: Kopf (Avatar, Name, Zeit), Antwort-Vorschau, Inhalt, Anhänge, Embeds, Reaktionen, Aktionen. */
import { MessageFlags, reactionKey, type ChannelDto, type MessageDto } from '@lagerfeuer/shared';
import {
  AlarmClock,
  ArrowRight,
  Bookmark,
  Copy,
  CornerUpLeft,
  Hash,
  Link2,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  Phone,
  Pin,
  PinOff,
  Puzzle,
  Reply,
  SmilePlus,
  Sparkles,
  Trash2,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import { memo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { EmojiPickerPopover } from '../../components/EmojiPicker';
import { Menu, anchorFrom, type Anchor, type MenuItem } from '../../components/Popover';
import { StickerImage } from '../../components/StickerPicker';
import { Tooltip } from '../../components/Tooltip';
import { isEmojiOnly } from '../../lib/markdown';
import { pathForChannel } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName, memberColor } from '../../store/selectors';
import { extraMenuItems } from '../extras';
import { copyText, deleteMessage, messageLink, setPinned, useMessageDialog } from './actions';
import { Attachments } from './Attachments';
import { Embeds } from './Embeds';
import { formatFullTime, formatMessageTime, formatShortTime } from './format';
import { isSystemMessage } from './grouping';
import { isShiftHeld, useShiftHeld } from './hooks';
import { Markdown } from './Markdown';
import { MessageEditor } from './MessageEditor';
import type { MessagePerms } from './perms';
import { addReaction, Reactions } from './Reactions';
import { focusComposer, useMessagesUi } from './state';

export interface MessageItemProps {
  message: MessageDto;
  grouped: boolean;
  channel: ChannelDto;
  perms: MessagePerms;
  compact?: boolean;
  panel?: boolean;
  highlighted?: boolean;
  onJump(messageId: string): void;
}

/** Anzeigedaten des Autors (Webhook-Overrides, Server-Nickname/-Avatar, Rollenfarbe). */
export function useAuthor(m: Pick<MessageDto, 'author' | 'member' | 'webhookId' | 'overrideName' | 'overrideAvatarUrl'>, serverId: string | null) {
  const member = useAppStore((s) => (serverId && m.author ? s.members[serverId]?.[m.author.id] : undefined));
  const roles = useAppStore((s) => (serverId ? s.roles[serverId] : undefined));
  const user = useAppStore((s) => (m.author ? (s.users[m.author.id] ?? m.author) : null));
  // Live-Mitglied aus dem Store (SERVER_MEMBER_UPDATE) vor dem Schnappschuss in der Nachricht: sonst zeigen bestehende
  // Nachrichten einen neuen Server-Spitznamen/-Avatar/Rollenfarbe erst nach dem Neuladen.
  const mem = member ?? m.member;
  const name = m.webhookId ? (m.overrideName ?? user?.displayName ?? user?.username ?? 'Webhook') : displayName(user, mem);
  return {
    user,
    name,
    color: m.webhookId ? null : memberColor(roles, mem?.roleIds),
    avatarUrl: m.overrideAvatarUrl ?? mem?.serverAvatarUrl ?? null,
  };
}

function ReplyPreview({ message, serverId, onJump }: { message: MessageDto; serverId: string | null; onJump(id: string): void }) {
  const ref = message.referencedMessage;
  const author = useAuthor({ author: ref?.author ?? null, member: null, webhookId: null, overrideName: null, overrideAvatarUrl: null }, serverId);
  if (!message.replyTo) return null;
  let body: ReactNode;
  if (!ref) body = <span className="faint">Die Nachricht konnte nicht geladen werden.</span>;
  else if (ref.deleted) body = <span className="faint">Die ursprüngliche Nachricht wurde gelöscht.</span>;
  else
    body = (
      <>
        <span className="msg-reply-author" style={author.color ? { color: author.color } : undefined}>
          @{author.name}
        </span>
        <span className="msg-reply-content truncate">
          {ref.content ? <Markdown content={ref.content} serverId={serverId} inline /> : <span className="faint">Klicke, um den Anhang zu sehen</span>}
        </span>
      </>
    );
  return (
    <button type="button" className="msg-reply" onClick={() => onJump(message.replyTo!.messageId)} aria-label="Zur beantworteten Nachricht springen" data-testid="message-reply-preview">
      <CornerUpLeft size={14} className="msg-reply-icon" aria-hidden="true" />
      {ref && !ref.deleted && <Avatar user={author.user} size={16} />}
      {body}
    </button>
  );
}

function SystemBody({ message, serverId, onJump }: { message: MessageDto; serverId: string | null; onJump(id: string): void }) {
  const author = useAuthor(message, serverId);
  const target = useAppStore((s) => (message.mentions[0] ? s.users[message.mentions[0]] : undefined));
  const navigate = useNavigate();
  const name = <strong className="msg-system-name">{author.name}</strong>;
  const targetName = <strong>{target ? displayName(target) : 'jemanden'}</strong>;
  switch (message.type) {
    case 'MEMBER_JOIN':
      return <>{name} ist ans Lagerfeuer gekommen. Willkommen!</>;
    case 'PIN_ADDED':
      return (
        <>
          {name} hat eine Nachricht angeheftet.{' '}
          {message.replyTo && (
            <button type="button" className="btn-link" onClick={() => onJump(message.replyTo!.messageId)} data-testid="system-pin-jump">
              Nachricht ansehen
            </button>
          )}
        </>
      );
    case 'THREAD_CREATED':
      return (
        <>
          {name} hat einen Thread gestartet:{' '}
          {message.thread ? (
            <button
              type="button"
              className="btn-link"
              onClick={() => {
                const ch = useAppStore.getState().channels[message.thread!.id];
                void navigate(pathForChannel(ch ?? { id: message.thread!.id, serverId, type: 'PUBLIC_THREAD', parentId: message.channelId }));
              }}
              data-testid="system-thread-open"
            >
              {message.thread.name ?? message.content}
            </button>
          ) : (
            <strong>{message.content}</strong>
          )}
        </>
      );
    case 'CHANNEL_NAME_CHANGE':
      return (
        <>
          {name} hat den Kanalnamen geändert: <strong>{message.content}</strong>
        </>
      );
    case 'RECIPIENT_ADD':
      return (
        <>
          {name} hat {targetName} zur Gruppe hinzugefügt.
        </>
      );
    case 'RECIPIENT_REMOVE':
      return target && target.id === message.author?.id ? <>{name} hat die Gruppe verlassen.</> : <>{name} hat {targetName} aus der Gruppe entfernt.</>;
    case 'CALL':
      return <>{name} hat einen Anruf gestartet.</>;
    default:
      return <Markdown content={message.content} serverId={serverId} inline />;
  }
}

const SYSTEM_ICONS: Partial<Record<MessageDto['type'], ReactNode>> = {
  MEMBER_JOIN: <ArrowRight size={16} />,
  PIN_ADDED: <Pin size={16} />,
  THREAD_CREATED: <MessageSquareText size={16} />,
  CHANNEL_NAME_CHANGE: <Pencil size={16} />,
  RECIPIENT_ADD: <UserPlus size={16} />,
  RECIPIENT_REMOVE: <UserMinus size={16} />,
  CALL: <Phone size={16} />,
};

function MessageItemImpl({ message: m, grouped, channel, perms, compact, panel, highlighted, onJump }: MessageItemProps) {
  const serverId = channel.serverId;
  const meId = useAppStore((s) => s.me?.id);
  const myRoleIds = useAppStore((s) => (serverId ? s.servers[serverId]?.me.roleIds : undefined));
  const showEmbeds = useAppStore((s) => s.settings?.showEmbeds ?? true);
  const editing = useMessagesUi((s) => s.editing[channel.id] === m.id);
  const author = useAuthor(m, serverId);
  const navigate = useNavigate();
  const shift = useShiftHeld();
  const [menu, setMenu] = useState<Anchor | null>(null);
  const [picker, setPicker] = useState<{ anchor: Anchor; super: boolean } | null>(null);
  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);

  const own = !!meId && m.author?.id === meId && !m.webhookId;
  const system = isSystemMessage(m);
  const canEdit = own && !m.deleted && !system && !perms.readOnly;
  const canDelete = !m.deleted && (own || perms.canManage) && !(system && !perms.canManage);
  const mentioned =
    !!meId &&
    !own &&
    (m.mentions.includes(meId) || m.mentionEveryone || m.mentionRoles.some((r) => myRoleIds?.includes(r)) || (!!m.referencedMessage && m.referencedMessage.author?.id === meId));

  const openProfile = (el: Element) => {
    if (m.author && !m.webhookId) overlays.profile(m.author.id, anchorFrom(el), serverId);
  };

  const reply = () => {
    useMessagesUi.getState().setReply(channel.id, m.id);
    focusComposer(channel.id);
  };
  const edit = () => useMessagesUi.getState().setEditing(channel.id, m.id);
  const remove = (skipConfirm: boolean) => {
    if (skipConfirm) void deleteMessage(m);
    else useMessageDialog.getState().open({ kind: 'delete', message: m });
  };
  const openThread = () => {
    if (m.thread) {
      const ch = useAppStore.getState().channels[m.thread.id];
      void navigate(pathForChannel(ch ?? { id: m.thread.id, serverId, type: 'PUBLIC_THREAD', parentId: channel.id }));
    } else useMessageDialog.getState().open({ kind: 'thread', message: m });
  };

  const extras = extraMenuItems.filter((i) => i.slot === 'messageMenu');
  const menuItems: MenuItem[] = [
    { id: 'react', label: 'Reaktion hinzufügen', icon: <SmilePlus size={16} />, hidden: !perms.canReact || m.deleted, onSelect: () => menu && setPicker({ anchor: menu, super: false }) },
    { id: 'super', label: 'Super-Reaktion', icon: <Sparkles size={16} />, hidden: !perms.canSuper || m.deleted, onSelect: () => menu && setPicker({ anchor: menu, super: true }) },
    { id: 'reply', label: 'Antworten', icon: <Reply size={16} />, hidden: !perms.canSend || system || m.deleted, onSelect: reply },
    { id: 'edit', label: 'Bearbeiten', icon: <Pencil size={16} />, hidden: !canEdit, onSelect: edit },
    { id: 'thread', label: m.thread ? 'Thread öffnen' : 'Thread erstellen', icon: <MessageSquareText size={16} />, hidden: !m.thread && (!perms.canThread || system || m.deleted), onSelect: openThread },
    { id: m.pinned ? 'unpin' : 'pin', label: m.pinned ? 'Lösen' : 'Anheften', icon: m.pinned ? <PinOff size={16} /> : <Pin size={16} />, hidden: !perms.canPin || system || m.deleted, onSelect: () => void setPinned(m, !m.pinned) },
    { id: 'copy-text', label: 'Text kopieren', icon: <Copy size={16} />, hidden: !m.content || m.deleted, separatorBefore: true, onSelect: () => void copyText(m.content, 'Text') },
    { id: 'copy-link', label: 'Link kopieren', icon: <Link2 size={16} />, onSelect: () => void copyText(messageLink(channel, m.id), 'Link') },
    { id: 'copy-id', label: 'ID kopieren', icon: <Hash size={16} />, onSelect: () => void copyText(m.id, 'ID') },
    ...extras.map<MenuItem>((it, i) => ({
      id: `extra-${it.id}`,
      label: it.label,
      icon: it.id === 'bookmark' ? <Bookmark size={16} /> : it.id === 'remind-me' ? <AlarmClock size={16} /> : <Puzzle size={16} />,
      separatorBefore: i === 0,
      onSelect: () => {
        const ctx = { serverId: serverId ?? undefined, channelId: channel.id, messageId: m.id };
        if (it.to) void navigate(it.to.replace(':serverId', serverId ?? '@me').replace(':channelId', channel.id).replace(':messageId', m.id));
        else overlays.open({ type: 'extra', item: it, ctx });
      },
    })),
    { id: 'delete', label: 'Nachricht löschen', icon: <Trash2 size={16} />, danger: true, separatorBefore: true, hidden: !canDelete, onSelect: () => remove(isShiftHeld()) },
  ];

  const onContextMenu = (e: React.MouseEvent) => {
    if ((e.target as Element).closest('a, video, audio, textarea')) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    const pos = { x: t.clientX, y: t.clientY };
    longPress.current = setTimeout(() => setMenu(pos), 500);
  };
  const cancelLongPress = () => {
    if (longPress.current) clearTimeout(longPress.current);
    longPress.current = null;
  };

  const cls = [
    'msg',
    grouped ? 'msg-grouped' : '',
    system ? 'msg-system' : '',
    mentioned ? 'msg-mentioned' : '',
    highlighted ? 'msg-highlight' : '',
    compact ? 'msg-compact' : '',
    panel ? 'msg-panel' : '',
    menu || picker ? 'msg-active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const time = (
    <time className="msg-time" dateTime={m.createdAt} title={formatFullTime(m.createdAt)} data-testid="message-timestamp">
      {compact || grouped ? formatShortTime(m.createdAt) : formatMessageTime(m.createdAt)}
    </time>
  );

  const actions = !m.deleted && (
    <div className="msg-actions" role="toolbar" aria-label="Nachrichtenaktionen" data-testid="message-actions">
      {perms.canReact && (
        <Tooltip label="Reaktion hinzufügen">
          <button className="icon-btn" onClick={(e) => setPicker({ anchor: anchorFrom(e.currentTarget), super: false })} data-testid="message-action-react">
            <SmilePlus size={18} />
          </button>
        </Tooltip>
      )}
      {perms.canSuper && !system && (
        <Tooltip label="Super-Reaktion">
          <button className="icon-btn" onClick={(e) => setPicker({ anchor: anchorFrom(e.currentTarget), super: true })} data-testid="message-action-super">
            <Sparkles size={18} />
          </button>
        </Tooltip>
      )}
      {perms.canSend && !system && (
        <Tooltip label="Antworten">
          <button className="icon-btn" onClick={reply} data-testid="message-action-reply">
            <Reply size={18} />
          </button>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip label="Bearbeiten">
          <button className="icon-btn" onClick={edit} data-testid="message-action-edit">
            <Pencil size={18} />
          </button>
        </Tooltip>
      )}
      {!panel && (m.thread || (perms.canThread && !system)) && (
        <Tooltip label={m.thread ? 'Thread öffnen' : 'Thread erstellen'}>
          <button className="icon-btn" onClick={openThread} data-testid="message-action-thread">
            <MessageSquareText size={18} />
          </button>
        </Tooltip>
      )}
      {shift && canDelete && (
        <Tooltip label="Sofort löschen">
          <button className="icon-btn danger" onClick={() => remove(true)} data-testid="message-action-delete">
            <Trash2 size={18} />
          </button>
        </Tooltip>
      )}
      <Tooltip label="Mehr">
        <button className="icon-btn" aria-haspopup="menu" aria-expanded={!!menu} onClick={(e) => setMenu(anchorFrom(e.currentTarget))} data-testid="message-action-more">
          <MoreHorizontal size={18} />
        </button>
      </Tooltip>
    </div>
  );

  const overlaysNode = (
    <>
      {menu && <Menu anchor={menu} items={menuItems} onClose={() => setMenu(null)} testId="message-menu" />}
      {picker && (
        <EmojiPickerPopover
          anchor={picker.anchor}
          onClose={() => setPicker(null)}
          serverId={serverId}
          allowExternal={perms.allowExternalEmojis}
          title={picker.super ? 'Super-Reaktion' : undefined}
          placement="left-start"
          onSelect={(e) => {
            const existing = m.reactions.find((r) => r.key === reactionKey(e));
            if (picker.super) void addReaction(channel.id, m.id, e, true);
            else if (!existing?.me) void addReaction(channel.id, m.id, e);
          }}
        />
      )}
    </>
  );

  const common = {
    className: cls,
    'data-testid': 'message',
    'data-message-id': m.id,
    'data-author-id': m.author?.id,
    'data-grouped': grouped ? 'true' : 'false',
    'data-type': m.type,
    onContextMenu,
    onTouchStart,
    onTouchEnd: cancelLongPress,
    onTouchMove: cancelLongPress,
    tabIndex: -1,
  } as const;

  if (system) {
    return (
      <li {...common}>
        <div className="msg-gutter msg-system-icon" aria-hidden="true">
          {SYSTEM_ICONS[m.type] ?? <ArrowRight size={16} />}
        </div>
        <div className="msg-main">
          <div className="msg-system-text" data-testid="message-content">
            <SystemBody message={m} serverId={serverId} onJump={onJump} /> {time}
          </div>
        </div>
        {actions}
        {overlaysNode}
      </li>
    );
  }

  const suppressEmbeds = (m.flags & MessageFlags.SUPPRESS_EMBEDS) !== 0 || !showEmbeds;
  const jumbo = !!m.content && isEmojiOnly(m.content);

  const header = !grouped && (
    <header className="msg-header">
      <button type="button" className="msg-author" style={author.color ? { color: author.color } : undefined} onClick={(e) => openProfile(e.currentTarget)} data-testid="message-author">
        {author.name}
      </button>
      {(m.webhookId || author.user?.isBot) && <span className="bot-tag">{m.webhookId ? 'WEBHOOK' : 'BOT'}</span>}
      {m.bridged && (
        <span className="msg-tag" title="Über die Brücke gespiegelt">
          gespiegelt
        </span>
      )}
      {time}
    </header>
  );

  return (
    <li {...common}>
      {m.replyTo && !compact && <ReplyPreview message={m} serverId={serverId} onJump={onJump} />}
      <div className="msg-row">
        <div className="msg-gutter">
          {grouped || compact ? (
            !compact && time
          ) : (
            <button type="button" className="msg-avatar" onClick={(e) => openProfile(e.currentTarget)} aria-label={`Profil von ${author.name}`} data-testid="message-avatar">
              <Avatar user={author.user} overrideUrl={author.avatarUrl} size={panel ? 32 : 40} />
            </button>
          )}
        </div>
        <div className="msg-main">
          {compact && (
            <span className="msg-compact-head">
              {time}{' '}
              {m.replyTo && <CornerUpLeft size={12} aria-label="Antwort" />}
            </span>
          )}
          {header}
          {editing ? (
            <MessageEditor message={m} />
          ) : m.deleted ? (
            <div className="msg-content msg-deleted" data-testid="message-content">
              Diese Nachricht wurde gelöscht.
            </div>
          ) : (
            m.content && (
              <div className="msg-content" data-testid="message-content">
                <Markdown content={m.content} serverId={serverId} jumbo={jumbo} />
                {m.editedAt && (
                  <span className="msg-edited" title={`Bearbeitet: ${formatFullTime(m.editedAt)}`} data-testid="message-edited">
                    (bearbeitet)
                  </span>
                )}
              </div>
            )
          )}
          {!m.deleted && m.stickers.length > 0 && (
            <div className="msg-stickers">
              {m.stickers.map((s) => (
                <span key={s.id} data-testid="message-sticker">
                  <StickerImage sticker={s} size={160} />
                </span>
              ))}
            </div>
          )}
          {!m.deleted && <Attachments attachments={m.attachments} compact={panel} />}
          {!m.deleted && !suppressEmbeds && <Embeds embeds={m.embeds} serverId={serverId} />}
          {m.thread && (
            <button type="button" className="msg-thread-chip" onClick={openThread} data-testid="message-thread-chip">
              <MessageSquareText size={16} aria-hidden="true" />
              <span className="truncate">{m.thread.name}</span>
              <span className="faint small">{m.thread.thread ? `${m.thread.thread.messageCount} Nachrichten` : 'Thread'}</span>
            </button>
          )}
          {!m.deleted && <Reactions message={m} serverId={serverId} perms={{ canReact: perms.canReact, canSuper: perms.canSuper, allowExternal: perms.allowExternalEmojis }} />}
        </div>
      </div>
      {!editing && actions}
      {overlaysNode}
    </li>
  );
}

export const MessageItem = memo(MessageItemImpl);
